<template>
    <div>
        <div class="grid grid-cols-12 gap-6">
            <div class="hidden md:col-span-1 md:block" />
            <div class="col-span-12 text-left md:col-span-10">
                <h2>Co najdete v Pražském uličníku</h2>
                <p class="mt-5 mb-5 font-bold">
                    Co je a co není možné najít v naší databázi, jak je
                    strukturované heslo, aktualizace informací v databázi a
                    další praktické tipy.
                </p>

                <p class="font-bold">Hesla, která je možné vyhledat</p>
                <p class="mb-5">
                    V databázi Pražského uličníku naleznete všechny pražské
                    ulice evidované v
                    <a
                        href="https://vdp.cuzk.gov.cz/vdp/ruian/ulice?atfill="
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-blue-600 underline"
                    >
                        Registru územní identifikace, adres a nemovitostí
                    </a>
                    (RÚIAN), kromě nich však také ulice zaniklé (tedy ty,
                    jejichž název byl zrušen bez náhrady/přejmenování) a
                    překvapivě i ulice nevzniklé (ty, které byly úředně
                    pojmenovány, avšak nikdy nevznikly).
                </p>
                <p class="mb-5">
                    Aktuální počet všech ulic v databázi se Vám zobrazí pod
                    abecedním seznamem ulic (v sekci Přehledy), nezapnete-li
                    nějaký filtr. Aktuální počet ulic evidovaných v RÚIAN se Vám
                    zobrazí v článku počty ulic (v sekci Historie ulic).
                </p>
                <p class="mb-5">
                    Kromě těchto základních hesel je možné vyhledat i starší
                    názvy ulic, které jsou obsaženy v rubrikách databázových
                    záznamů. Jsou zde všechny starší názvy, které se nám
                    podařilo dohledat, včetně německých (jak z doby okupace, tak
                    historické z doby před rokem 1918) a výjimečně i latinských.
                </p>
                <p class="mb-5">
                    Databáze bude pravidelně
                    <span class="font-bold"
                        >aktualizována editory z odboru Archivu HMP</span
                    >
                    , takže v relativně krátké době se zde objeví i nové uliční
                    názvy či přejmenování.
                </p>
                <p class="mb-5">
                    Základní cílem aplikace je zpřístupnit informaci o konkrétní
                    ulici, kterou zájemce hledá, vyhledávání je tedy orientováno
                    na jednotlivé názvy a nikoli na filtrování skupin ulic podle
                    různých hledisek. Jako jediný přehled tedy uvádíme abecední
                    seznam ulic s možností selekce podle katastru.
                </p>
                <p class="font-bold">Struktura hesla</p>
                <p class="mb-5">
                    Po vyhledání ulice se zobrazí v levé části mapa (viz níže),
                    pokud jde o existující ulici evidovanou v RÚIAN, zobrazí se
                    na mapě její poloha. V pravé části se objeví název ulice a
                    pod ním katastr(y), kde se nachází.
                </p>
                <p class="mb-5">
                    Následuje <span class="font-bold">Původ názvu</span>, kde je
                    vysvětleno, podle čeho se ulice jmenuje. Naší snahou bylo
                    vycházet z úředních pramenů, avšak zdaleka ne pro všechny
                    ulice takový pramen existuje, pročež zde můžete najít i
                    informaci:
                    <span class="italic">„Původ názvu nezjištěn“</span>.
                </p>
                <p class="mb-5">
                    V další části hesla označené
                    <span class="font-bold">Historie</span> najdeme nejstarší
                    názvy ulic z období před jejich úředním pojmenováním, tyto
                    názvy mnohdy platily paralelně a lze je těžko přesněji
                    časově zařadit. Proto tato položka není strukturovaná,
                    přesto jednotlivé názvy vyznačené tučně lze vyhledat
                    nástrojem vyhledávání. V této položce se také snažíme
                    vysvětlit vztahy jednotlivých historických názvů, pokud na
                    sebe nenavazují (ulice se skládá z úseků, které byly spojeny
                    či rozděleny).
                </p>
                <p class="mb-5">
                    Následuje položka
                    <span class="font-bold">Viz též</span>, kde jsou odkazy na
                    ulice či souborná hesla (viz níže), které mají k heslu
                    vztah.
                </p>
                <p class="mb-5">
                    Poté již následují chronologicky seřazené
                    <span class="font-bold">Historické názvy</span>. U každého
                    je, pokud možno, uveden časový rozsah, kdy platil a jeho
                    původ. Doplňující informace, které budou v aplikaci v
                    budoucnu rozvíjeny (u nově doplněných, přejmenovaných či
                    opravených ulic) následují v položkách
                    <span class="font-bold">Pramen</span> (převážně půjde o
                    usnesení rady hl. m. Prahy, která ulice pojmenovává),
                    <span class="font-bold">Dokument</span>,
                    <span class="font-bold">Osobnost</span> (zde budeme
                    odkazovat na
                    <a
                        href="https://biography.hiu.cas.cz/wiki/Hlavní_strana"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-blue-600 underline"
                    >
                        Biografický slovník českých zemí </a
                    >,
                    <a
                        href="https://cs.wikipedia.org/wiki/Hlavní_strana"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-blue-600 underline"
                        >Wikipedii</a
                    >
                    či další renomované internetové zdroje) a
                    <span class="font-bold">Budova</span>.
                </p>
                <p class="mb-5">
                    Samostatnou kapitolou je položka
                    <span class="font-bold">Obrázek</span>. Uvádíme dva druhy
                    obrázků. První jsou uložené přímo v naší databázi – u těchto
                    se zobrazí náhled v pravém horním rohu na začátku záznamu a
                    v případě, že obrázků je více, jejich přehled v dolní části
                    záznamu. Po kliknutí na kterýkoli z nich se objeví galerie,
                    ve které lze listovat. Druhé jsou ty, které jsou uloženy v
                    externích zdrojích. Jedná se zejména o
                    <a
                        href="https://katalog.ahmp.cz/pragapublica/"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-blue-600 underline"
                        >Archivní katalog</a
                    >
                    Archivu hl. m. Prahy, kde se nacházejí tisíce historických
                    fotografií. U těchto obrázků je zobrazena tato ikona
                    <img
                        src="../../public/img/map_catalog.svg"
                        alt=""
                        class="ml-1 inline h-8 w-8 object-contain"
                    />
                    . Po kliknutí na ni se v novém okně otevře odkaz na obraz ve
                    zdrojové aplikaci.
                </p>
                <p class="font-bold">Mapa</p>
                <p class="mb-5">
                    V levé části okna se zobrazí ulice (evidovaná v RÚIAN) na
                    mapě ve výřezu přizpůsobeném její velikosti. V případě
                    Souborného hesla se zobrazí všechny ulice, které heslo
                    obsahuje. V mapě jde přepnout podkladová vrstva, vybrat
                    jednu z historických map, nebo zvolit jejich překryv.
                    Prozatím bohužel není možné zobrazit zaniklé ulice či
                    historické názvy vzhledem k obtížnosti jejich přesného
                    vymezení. Je třeba mít na paměti, že nejstarší plány
                    zachycují pouze centrum dnešní Prahy a v okrajových částech
                    se nezobrazují (vyjma stabilního katastru z roku 1842, který
                    pokrývá celou Prahu). Na mapě jsou znázorněny hnědou barvou
                    linie okolních ulic. Pokud se na konkrétní ulici klikne,
                    zobrazí se v pravém okně heslo příslušné ulice. Jde tedy
                    „listovat“ ulicemi podle jejich polohy na mapě.
                </p>
                <p class="font-bold">Souborná hesla</p>
                <p class="mb-5">
                    V případě uličních názvoslovných systémů (blíže článek
                    Lokální názvoslovné systémy v sekci Historie ulic) jsme
                    původně z důvodu úspory místa soustředili všechny společné
                    informace týkající se všech ulic v systému do tzv.
                    souborného hesla. Seznam všech souborných hesel lze nalézt v
                    sekci Přehledy, kde na ně lze kliknout a „rozbalit“ je. Na
                    konci hesla v položce
                    <span class="italic">Viz též</span> jsou odkazy na všechny
                    ulice k přímému prokliku. Souborné heslo není samostatným
                    názvem veřejného prostranství, a tudíž není možné jej
                    vyhledat nástrojem vyhledání. U každé ulice, které se
                    souborné heslo týká, je také uveden odkaz
                    <span class="italic">Viz též</span>, kterým se na něj
                    proklikneme.
                </p>
                <p class="font-bold">Články</p>
                <p class="mb-5">
                    V sekci <span class="font-bold">Historie ulic</span>,
                    <span class="font-bold">Prameny</span> a
                    <span class="font-bold">O aplikaci</span> v horním menu je
                    možno nalézt články pojednávající o historii pražského
                    uličního názvosloví, vývoji uličních tabulek, počtech ulic,
                    pramenech, ze kterých jsme čerpali atd. Nechybí také článek
                    o pravopisu názvů a informace o autorech a
                    spolupracovnících, kteří se na Pražském uličníku podíleli.
                    Jde o základní informace vycházející z úvodu tištěné
                    publikace.
                </p>
                <p class="font-bold">Kontakt</p>
                <p class="mb-5">
                    V kontaktním formuláři najdete dvě možnosti podle typu
                    dotazu: Obsah encyklopedie a Technické problémy.
                </p>
                <ul class="mb-25 list-disc pl-15">
                    <li class="mb-5">
                        Pokud narazíte na technické nedostatky či chyby v
                        aplikaci, využijte k jejich nahlášení sekci
                        <span class="italic">Technické problémy</span>.
                    </li>
                    <li>
                        V sekci
                        <span class="italic">Obsah encyklopedie</span> můžete
                        oslovit autory, pokud objevíte chybu v údajích, nebo
                        ještě lépe 😉 pokud máte k dispozici novou informaci o
                        aktuálním či historickém názvu. Připojíte-li k ní
                        důvěryhodný zdroj, rádi ji do databáze zapracujeme.
                    </li>
                </ul>

                <!-- next chapters -->
                <div class="mb-10 flex justify-between border-t-1 pt-5">
                    <p class="mb-5 w-1/2 border-r-1 font-bold">
                        <!-- nothing -->
                    </p>
                    <p class="mb-5 w-1/2 text-right font-bold">
                        <nuxt-link
                            to="/clanky/autori"
                            class="flex justify-end underline"
                        >
                            <span class="order-1 ml-2">Autoři</span>
                            <img
                                src="../../public/img/right-arrow.png"
                                alt=""
                                class="order-2 ml-2 inline h-5 w-5"
                            />
                        </nuxt-link>
                    </p>
                </div>
            </div>
            <div class="hidden md:col-span-1 md:block" />
        </div>

        <AppFooter />
    </div>
</template>

<script setup lang="ts"></script>

<style scoped></style>
